import { CarsAvailabilityActions } from "@b2bportal/core-cars";
import {
  BundleResponseEnum,
  PackageEndpointsV2,
  type PackagesCustomizeCarsAvailabilityRequest,
  type PackagesCustomizeCarsAvailabilityResponse,
} from "@b2bportal/core-packages";
import type { BundledPackageStateToVehicleId } from "@b2bportal/core-packages";
import { CodeContextEnum } from "@b2bportal/ground-api";
import { axiosInstance } from "@hopper-b2b/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { PackageSearchQueryParams } from "@tenant/types";
import { getPackageCarPrice } from "../../utilities";
import {
  formatPackageDynamicFieldDates,
  useFormatPackagePickupDropoffDates,
} from "@/pages/Packages/hooks";
import type { RootState } from "@/store";
import { PackageBaseSelectors } from "@/store";
import { PackageSearchSelectors } from "@/store/packages/selectors";
import { PackagesBaseActions } from "@/store/packages/slice/packages";
import { fetchPackageSearchParams } from "@/store/packages/thunks/general";

export const fetchPackageCarsAvailability = createAsyncThunk<
  { packageStateByVehicleId: BundledPackageStateToVehicleId },
  { search: PackageSearchQueryParams },
  { state: RootState; rejectValue: { error: string } }
>("packages/fetchCars", async ({ search }, thunkAPI) => {
  // const searchId = uuidv4();
  const state = thunkAPI.getState();
  let requestedSearch = PackageSearchSelectors.getSearchFormValues(state);
  const packageType = PackageBaseSelectors.getCurrentActivePackageType(state);

  try {
    if (
      !requestedSearch ||
      (!requestedSearch?.origin && !requestedSearch?.destination)
    ) {
      // Go grab the requested search from the search params
      requestedSearch = await thunkAPI
        .dispatch(fetchPackageSearchParams(search))
        .unwrap();
    }

    // Verify packageType and update if different
    if (search.packageType && search.packageType !== packageType) {
      thunkAPI.dispatch(
        PackagesBaseActions.setCurrentActivePackageType(search.packageType)
      );
    }

    if (requestedSearch?.origin && requestedSearch?.destination) {
      const { pickupDate, dropoffDate } = useFormatPackagePickupDropoffDates(
        requestedSearch.dateRange.from,
        requestedSearch.dateRange.until
      );
      const { startDate, endDate } = formatPackageDynamicFieldDates(
        requestedSearch.dateRange.from,
        requestedSearch.dateRange.until
      );

      const carsAvailabilityRequest: PackagesCustomizeCarsAvailabilityRequest =
        {
          customize: {
            bundledPackageStateToken: { value: search.packageToken },
            carAvailabilityRequest: {
              dynamicFields: {
                adjustStartDateUsingLanding: {
                  date: startDate,
                  originRegion: requestedSearch.origin.airportCode.code,
                },
                adjustEndDateUsingTakeoff: {
                  date: endDate,
                  originRegion: requestedSearch.destination.airportCode.code,
                },
              },
              pickupSelection: {
                CodeContext: CodeContextEnum.GooglePlace,
                code: requestedSearch.destination.groundSelection
                  .groundSelection.placeId,
              },
              dropOffSelection: {
                CodeContext: CodeContextEnum.GooglePlace,
                code: requestedSearch.destination.groundSelection
                  .groundSelection.placeId,
              },
              driverAge: requestedSearch.driverAge ?? 25,
              pickupDateTime: pickupDate,
              dropoffDateTime: dropoffDate,
            },
          },
        };

      const {
        data: carsAvailabilityResponse,
      }: { data: PackagesCustomizeCarsAvailabilityResponse } =
        await axiosInstance.post(
          PackageEndpointsV2.Customize,
          carsAvailabilityRequest
        );

      if (
        carsAvailabilityResponse.BundleResponse ===
        BundleResponseEnum.CarAvailabilityResponse
      ) {
        const { packageStateByVehicleId, payload } =
          carsAvailabilityResponse.carAvailability;

        thunkAPI.dispatch(
          CarsAvailabilityActions.setCarsAvailabilityFromPackages(payload)
        );

        // Initialize price filter state with incremental pricing min/max
        // This ensures the slider works correctly and doesn't show MAX_SAFE_INTEGER
        const carPrices = payload.vehicles
          .map((car) => getPackageCarPrice(car, packageStateByVehicleId))
          .filter((price) => price !== null);

        if (carPrices.length > 0) {
          const initialPriceOption = carPrices[0];
          const priceRange = carPrices.reduce(
            (acc, item) => ({
              min: item.value < acc.min.value ? item : acc.min,
              max: item.value > acc.max.value ? item : acc.max,
            }),
            { min: initialPriceOption, max: initialPriceOption }
          );

          // Set the filter state to actual min/max so slider works
          thunkAPI.dispatch(
            CarsAvailabilityActions.setPriceFilter({
              min: priceRange.min.value,
              max: priceRange.max.value,
            })
          );
        }

        return { packageStateByVehicleId };
      }
    } else {
      return thunkAPI.rejectWithValue({ error: "No origin or destination" });
    }
    // return carsAvailabilityResponse;
  } catch (e) {
    console.error("Failed to fetch cars:", e);
    const errorMessage = e instanceof Error ? e.message : String(e);
    // thunkAPI.dispatch(
    //   CarsAvailabilityActions.handleFetchInitialRejected({
    //     error: errorMessage,
    //   })
    // );
    return thunkAPI.rejectWithValue({ error: errorMessage });
  }
});
